import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faChevronDown } from "@fortawesome/free-solid-svg-icons"
import { fetchPointsInterets } from "../../store/actions/pointInteretActions"
import { fetchParc, fetchParcResume } from "../../store/actions/parcActions"
import { generalStyles as gs } from "../styles/styles"
import { tableStyles } from "../styles/tableStyles"
import { COLORS } from "../../utilities/Constants"

// Ancienneté (jours présents) → seuils repris de l'existant : <3 vert, 3–10
// orange, >10 rouge (api/parccolor = « en retard » à partir de ~10 j).
const agingBadge = (days) => {
	if (days < 3) return gs.badge(COLORS.success, COLORS.successLight)
	if (days < 10) return gs.badge(COLORS.warning, COLORS.warningLight)
	return gs.badge(COLORS.danger, COLORS.dangerLight)
}

const StatCard = ({ label, value, sub, accent, highlight }) => (
	<div style={highlight ? gs.totalHighlight : gs.statCard}>
		<div style={{ ...gs.statLabel, color: highlight ? "#cfc9c2" : COLORS.textLight }}>
			{label}
		</div>
		<div
			style={{
				...gs.statValue,
				color: highlight ? "#fff" : accent || COLORS.text,
			}}
		>
			{value}
		</div>
		{sub && (
			<div style={{ ...gs.statSub, color: highlight ? "#b8b2ab" : COLORS.textMuted }}>
				{sub}
			</div>
		)}
	</div>
)

const ParcV2 = () => {
	const dispatch = useDispatch()
	const history = useHistory()
	const parcs = useSelector((state) => state.pointInteretInfo.parcs)
	const parcsLoading = useSelector(
		(state) => state.pointInteretInfo.fetchPointsInterets.loading,
	)
	const { vehicules, resume, fetchParc: fetchParcState } = useSelector(
		(state) => state.parcInfo,
	)

	const [selected, setSelected] = useState(null)
	const [sortAsc, setSortAsc] = useState(false) // par défaut : plus vieux en haut

	useEffect(() => {
		dispatch(fetchPointsInterets())
		dispatch(fetchParcResume())
	}, [dispatch])

	// Sélectionne le 1er lot dès que la liste est chargée (corrige le bug de
	// closure périmée de l'ancienne version).
	useEffect(() => {
		if (selected === null && parcs.length > 0) setSelected(parcs[0].id)
	}, [parcs, selected])

	// Charge les véhicules du lot sélectionné.
	useEffect(() => {
		if (selected !== null) dispatch(fetchParc(selected))
	}, [dispatch, selected])

	useEffect(() => {
		const onKey = (e) => {
			if (e.key === "Escape") history.push("/")
		}
		document.addEventListener("keydown", onKey)
		return () => document.removeEventListener("keydown", onKey)
	}, [history])

	// Totaux tous lots (depuis le résumé) + plus vieux du lot courant.
	const totalStockes = useMemo(
		() => Object.values(resume).reduce((s, r) => s + r.total, 0),
		[resume],
	)
	const totalRetard = useMemo(
		() => Object.values(resume).reduce((s, r) => s + r.en_retard, 0),
		[resume],
	)
	const plusVieux = vehicules.length
		? Math.round(Math.max(...vehicules.map((v) => v.color)))
		: 0

	const sorted = useMemo(() => {
		const dir = sortAsc ? 1 : -1
		return [...vehicules].sort((a, b) => dir * (a.color - b.color))
	}, [vehicules, sortAsc])

	const openFiche = (id) =>
		history.push(`/depannage/fiche/${id}`, { from: "parc" })

	return (
		<div style={gs.page}>
			<div style={styles.shell}>
				<div style={gs.header}>
					<div>
						<div style={gs.ficheNumber}>Parc</div>
						<div style={gs.title}>Véhicules stockés</div>
					</div>
				</div>

				{parcsLoading ? (
					<div style={styles.loading}>
						<FontAwesomeIcon icon={faSync} spin /> Chargement…
					</div>
				) : (
					<>
						{/* Récap tous lots */}
						<div style={styles.statsGrid}>
							<StatCard label="Total stockés" value={totalStockes} highlight />
							<StatCard
								label="En retard (> 10 j)"
								value={totalRetard}
								accent={totalRetard > 0 ? COLORS.danger : COLORS.text}
								sub="tous lots confondus"
							/>
							<StatCard
								label="Plus vieux (lot courant)"
								value={`${plusVieux} j`}
							/>
						</div>

						{/* Sélecteur de lots + badges compteur */}
						<div style={styles.lotRow}>
							{parcs.map((p) => {
								const r = resume[String(p.id)] || {
									total: 0,
									en_retard: 0,
								}
								const active = selected === p.id
								return (
									<button
										key={p.id}
										type="button"
										style={styles.lotBtn(active)}
										onClick={() => setSelected(p.id)}
									>
										{p.label}
										<span style={styles.lotCount(active)}>{r.total}</span>
										{r.en_retard > 0 && (
											<span
												style={styles.lotRetard}
												title={`${r.en_retard} en retard`}
											>
												{r.en_retard}
											</span>
										)}
									</button>
								)
							})}
						</div>

						{fetchParcState.loading ? (
							<div style={styles.loading}>
								<FontAwesomeIcon icon={faSync} spin /> Chargement du lot…
							</div>
						) : sorted.length > 0 ? (
							<div style={tableStyles.container}>
								<table style={tableStyles.table}>
									<thead>
										<tr>
											<th style={tableStyles.th}>N°</th>
											<th style={tableStyles.th}>Marque</th>
											<th style={tableStyles.th}>Plaques</th>
											<th style={tableStyles.th}>Date d'arrivée</th>
											<th style={tableStyles.th}>
												<button
													type="button"
													style={styles.sortBtn}
													onClick={() => setSortAsc((v) => !v)}
												>
													Jours présents
													<FontAwesomeIcon
														icon={faChevronDown}
														style={{
															fontSize: 10,
															marginLeft: 6,
															transform: sortAsc
																? "rotate(180deg)"
																: "none",
														}}
													/>
												</button>
											</th>
										</tr>
									</thead>
									<tbody>
										{sorted.map((v, index) => (
											<Row
												key={`veh_${v.id}_${index}`}
												v={v}
												index={index}
												onOpen={() => openFiche(v.id)}
											/>
										))}
									</tbody>
								</table>
							</div>
						) : (
							<div style={tableStyles.container}>
								<div style={styles.empty}>
									Aucun véhicule dans ce lot
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	)
}

const Row = ({ v, index, onOpen }) => {
	const [hover, setHover] = useState(false)
	return (
		<tr
			onDoubleClick={onOpen}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				cursor: "pointer",
				backgroundColor: hover ? tableStyles.rowHover : tableStyles.rowBg(index),
				transition: "background-color 0.12s",
			}}
		>
			<td style={tableStyles.td}>{index + 1}</td>
			<td style={tableStyles.td}>{v.marque}</td>
			<td style={tableStyles.td}>{v.plaques}</td>
			<td style={tableStyles.td}>{v.date}</td>
			<td style={tableStyles.td}>
				<span style={agingBadge(v.color)} title={v.diff}>
					{Math.round(v.color)} j
				</span>
			</td>
		</tr>
	)
}

const styles = {
	shell: { width: "100%", maxWidth: 1100, margin: "0 auto" },
	loading: { padding: "50px 0", textAlign: "center", color: COLORS.textMuted },
	statsGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
		gap: 14,
		marginBottom: 20,
	},
	lotRow: {
		display: "flex",
		flexWrap: "wrap",
		gap: 8,
		marginBottom: 20,
	},
	lotBtn: (active) => ({
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
		padding: "8px 14px",
		borderRadius: 10,
		border: `1px solid ${active ? COLORS.text : COLORS.border}`,
		backgroundColor: active ? COLORS.text : COLORS.surface,
		color: active ? "#fff" : COLORS.text,
		fontSize: 13,
		fontWeight: 500,
		cursor: "pointer",
		fontFamily: "inherit",
		transition: "all 0.12s",
	}),
	lotCount: (active) => ({
		fontSize: 12,
		fontWeight: 600,
		color: active ? "#fff" : COLORS.textMuted,
		backgroundColor: active ? "rgba(255,255,255,0.18)" : COLORS.bg,
		borderRadius: 99,
		padding: "1px 8px",
	}),
	lotRetard: {
		fontSize: 11,
		fontWeight: 700,
		color: "#fff",
		backgroundColor: COLORS.danger,
		borderRadius: 99,
		padding: "1px 7px",
	},
	sortBtn: {
		border: "none",
		background: "transparent",
		padding: 0,
		font: "inherit",
		fontSize: 11,
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: "0.06em",
		color: COLORS.textLight,
		cursor: "pointer",
		display: "inline-flex",
		alignItems: "center",
	},
	empty: {
		...tableStyles.empty,
		padding: "40px 16px",
		color: COLORS.textLight,
		fontSize: 14,
	},
}

export default ParcV2
